/**
 * The message from anything thrown — never the thing itself. For text that ends up in state or on
 * screen; logs use `describeError` below.
 *
 * An `AxiosError` carries `config.headers`, and the request interceptor puts the user's
 * `Authorization: Bearer <token>` there, so handing a caught error to `console.*` prints their
 * credential into the console.
 */
export const errorMessage = (error: unknown): string => {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	// The CometChat SDK rejects with a plain `{ code, message, details }`, and axios responses carry
	// the API's own `{ message }`. Without this they would all log as "[object Object]".
	if (error && typeof error === "object") {
		const { code, message } = error as { code?: unknown; message?: unknown };
		if (typeof message === "string" && message) {
			return typeof code === "string" && code ? `${code}: ${message}` : message;
		}
	}
	return String(error);
};

interface RequestErrorLike {
	isAxiosError?: boolean;
	config?: { method?: string; url?: string; baseURL?: string };
	response?: { status?: number; data?: unknown };
}

const SERVER_MESSAGE_MAX = 200;

/** The API's own explanation: NestJS sends `{ message }`, validation errors a list of them. */
const serverMessage = (data: unknown): string => {
	const message = (data as { message?: unknown } | null)?.message;
	const text = Array.isArray(message) ? message.join("; ") : message;
	return typeof text === "string" ? text.slice(0, SERVER_MESSAGE_MAX) : "";
};

/** Path only: the query string can carry what the reader typed (search), the host adds nothing. */
const requestPath = (url?: string, baseURL?: string): string => {
	if (!url) return "";
	try {
		return new URL(url, baseURL || "http://local").pathname;
	} catch {
		return url.split("?")[0];
	}
};

/**
 * For `console.*`: what failed and how — `[500] GET /v1/courses/1 — Course not found` — built from
 * status, method, path and the server's message only. Headers and request bodies never reach it.
 * Anything that is not a failed request is logged as `errorMessage` would show it.
 */
export const describeError = (error: unknown): string => {
	const request = error as RequestErrorLike | null;
	if (!request || typeof request !== "object" || !(request.isAxiosError || request.config)) {
		return errorMessage(error);
	}

	const status = request.response?.status ?? "no response";
	const method = request.config?.method?.toUpperCase() ?? "";
	const path = requestPath(request.config?.url, request.config?.baseURL);
	const text = serverMessage(request.response?.data) || errorMessage(error);
	const target = [method, path].filter(Boolean).join(" ");

	return `[${status}]${target ? ` ${target}` : ""} — ${text}`;
};
